import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { Company, CompanyApplication } from '../../../../../interfaces/users.interface';
import { CompanyTabListParams } from '../../../../../interfaces/companyDetail.interface';
import { getCompanyApps } from '../../../../../services/companyDetail';
import { useCompanyResource } from '../../../../Vendors/useCompanyResource';
import { DataCell, DataRow } from '../../../../Tables/DataList';
import { PaginationBar, DEFAULT_PER_PAGE, TabEmpty, TabSkeletonRows } from '../../../../Vendors/companyTabUi';
import LicensedApplicationDataRowV2 from './LicensedApplicationDataRowV2';

// Server-paginated licensed apps (application not owned by this company), each
// row carrying its embedded certifications — no full-list fetch.
const licensedFetcher = (id: number | string, params?: CompanyTabListParams) =>
  getCompanyApps(id, { ...params, kind: 'licensed' });

type LicensedApplicationsCardsProps = {
  company: Company;
  userCanAdminCompany: boolean;
  companyCallback: any;
  reloadToken?: number;
  search?: string;
};

const LicensedApplicationsCards = (props: LicensedApplicationsCardsProps) => {
  const { company, userCanAdminCompany, companyCallback, reloadToken, search } = props;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PER_PAGE);
  const { items: applications, count, loading, refetch } = useCompanyResource<CompanyApplication>(
    licensedFetcher,
    company.id,
    page,
    pageSize,
    true,
    search,
  );

  useEffect(() => {
    setPage(1);
  }, [search]);

  // Re-fetch this page when the tab-level add modal reports a change.
  useEffect(() => {
    if (reloadToken) refetch();
  }, [reloadToken]);

  // Keep the parent in sync (header/labels) and refetch this page after a change.
  const handleChange = useCallback((updatedCompany: Company) => {
    companyCallback(updatedCompany);
    refetch();
  }, [companyCallback, refetch]);

  return (
    <Box>
      <Typography variant='h6' sx={{ m: '2em -16px 0px -16px', background: grey[300], padding: '.5em 1.5em' }}>
        3rd Party Licensed Application
      </Typography>
      <DataRow header sx={{ mt: 1 }}>
        <DataCell xs={1} textAlign='center'>Shield</DataCell>
        <DataCell xs={2}>Name</DataCell>
        <DataCell xs={1}>Version / Apps</DataCell>
        <DataCell xs={1.5}>Sites</DataCell>
        <DataCell xs={2}>Services</DataCell>
        <DataCell xs={1.5}>Configuration Guidelines</DataCell>
        <DataCell xs={1.5}>Application Types</DataCell>
        <DataCell xs={1}>&nbsp;</DataCell>
      </DataRow>

      {loading && <TabSkeletonRows rows={4} columns={[1, 2, 1, 1.5, 2, 1.5, 1.5, 1]} />}

      {!loading && applications.length === 0 && <TabEmpty label='No licensed applications' />}

      {!loading && applications.map((companyApplication: CompanyApplication) => (
        <LicensedApplicationDataRowV2
          key={companyApplication.id}
          company={company}
          companyApplication={companyApplication}
          companySuccessCallback={handleChange}
          userCanAdminCompany={userCanAdminCompany}
        />
      ))}

      <PaginationBar variant='inline' count={count} page={page} pageSize={pageSize} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
    </Box>
  );
};

export default LicensedApplicationsCards;
